import { ReactComponent as Logo } from "../../assets/svg/logo.svg";
import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { CustomTextField } from "../../components/CustomTextField/CustomTextField";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  LoginDenial,
  useRegisterFleetUser,
  useSendResetCode,
  useUserLogin,
} from "../../api/user";
import { useGetInvitation, resolveNextAction } from "../../api/invitation";
import {
  describePolicy,
  usePasswordPolicy,
  validatePassword,
} from "../../api/passwordPolicy";
import { useLocation, useNavigate } from "react-router";
import { isMobile } from "react-device-detect";

/**
 * AppsFlyer OneLink — smart URL that opens the CodeNekt mobile app when it's
 * installed, or the appropriate app store otherwise. Kept in one place so it
 * can later move to an env var (REACT_APP_APP_ONELINK).
 */
const APP_ONELINK = "https://onelink.to/euv9bm";

/**
 * Ecran d'accueil d'une invitation ("confirmation d'ajout").
 *
 * Cible du lien envoye par mail : /invitation?email=...&code=...&company=...
 *
 * ── CE QUI A CHANGE, ET POURQUOI ────────────────────────────────────────────
 *
 * Cet ecran decidait lui-meme quoi montrer, a partir de `user_exists` — un
 * booleen qui valait `is_active = 1` sur la table `user`. Mais "le compte n'est
 * pas desactive" ne dit RIEN de "cette personne connait un mot de passe". Les
 * deux divergent des qu'un compte est active sans qu'aucun identifiant ne soit
 * pose : un compte CodeNekt preexistant, un pre-compte cree par l'invitation,
 * un rattachement fait depuis l'ecran Accueil de l'Admin.
 *
 * Quand ils divergeaient, cette page affichait "vous avez deja un compte,
 * connectez-vous simplement" a quelqu'un qui n'avait aucun mot de passe, avec
 * pour seule sortie un bouton vers la page de connexion — et pas le moindre
 * lien "mot de passe oublie". L'invitation restait `pending` jusqu'a expiration
 * et l'administrateur lisait "Nombre d'utilisateurs : 0" sans comprendre.
 *
 * La decision vient desormais du serveur (`next_action`, voir
 * InvitationService::getByCode()), et CHAQUE etat de cette page a une sortie :
 *
 *   set_password      formulaire de mot de passe, puis entree directe
 *   sign_in           connexion en ligne, adresse pre-remplie + lien de
 *                     reinitialisation
 *   already_member    deja rattache : on entre
 *   already_accepted  invitation consommee : connexion
 *   expired/revoked   etat terminal, mais avec la marche a suivre
 */
export const Invitation: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");
  const code = queryParams.get("code");
  const company = queryParams.get("company");

  const { invitation, isLoading, isError } = useGetInvitation(code);
  const nextAction = resolveNextAction(invitation);

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  // Branche `sign_in` : mot de passe du compte existant, et message persistant
  // si l'authentification aboutit sans ouvrir de flotte.
  const [signInPassword, setSignInPassword] = useState("");
  const [denial, setDenial] = useState<LoginDenial | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const { mutate: register, isPending, isSuccess } = useRegisterFleetUser();
  const { mutate: login, isPending: isSigningIn } = useUserLogin(setDenial);
  const { mutate: sendResetCode, isPending: isSendingReset } = useSendResetCode();

  /*
   * La regle vient du serveur, elle n'est plus recopiee ici.
   *
   * Cet ecran portait sa propre `validatePassword` : 8 caracteres, minuscule,
   * majuscule, chiffre, symbole. L'API, elle, n'exigeait que 8 caracteres, et
   * l'ecran "mot de passe oublie" n'exigeait rien. Trois regles pour un meme
   * geste, dont la plus stricte s'appliquait la ou elle ne protege rien.
   */
  const passwordPolicy = usePasswordPolicy();

  /*
   * L'invite arrive depuis un mail : on ecarte une session precedente pour que
   * la page ne melange pas deux comptes.
   *
   * `localStorage.clear()` faisait bien plus que ca — il emportait la langue
   * choisie et toute autre preference stockee par l'application. On ne retire
   * que ce qui porte l'identite.
   */
  useEffect(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("currentUser");
  }, []);

  // Pre-fill name from the invitation (admin may have set it)
  useEffect(() => {
    if (invitation?.firstname && !firstname) setFirstname(invitation.firstname);
    if (invitation?.lastname && !lastname) setLastname(invitation.lastname);
  }, [invitation]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Le serveur a ouvert la session pendant l'acceptation
   * (UserController@addFleetAdmin termine par un login), et useRegisterFleetUser
   * la conserve desormais. Il n'y a donc plus aucune raison de demander a
   * quelqu'un de ressaisir le mot de passe qu'il vient de choisir : on entre.
   *
   * L'ecran de succes ci-dessous ne sert plus que de repli, quand l'API n'a pas
   * renvoye de jeton exploitable.
   */
  useEffect(() => {
    if (isSuccess && localStorage.getItem("token")) {
      window.location.href = "/dashboard";
    }
  }, [isSuccess]);

  /*
   * Le nom n'est demande que s'il manque VRAIMENT.
   *
   * La condition testait le role (`role === "EMPLOYEE"`), en s'appuyant sur le
   * fait que les comptes FLEET_MANAGER sont pre-crees. C'est un detail
   * d'implementation du serveur, et il est en train de disparaitre. La vraie
   * question est : est-ce que je connais deja le nom de cette personne ?
   */
  const needsName =
    nextAction === "set_password" &&
    !(invitation?.firstname?.trim() && invitation?.lastname?.trim());

  // Sortie mobile : ouvrir l'application si elle est installee, sinon le store.
  const handleOpenApp = () => {
    if (isMobile) {
      window.location.href = APP_ONELINK;
    } else {
      navigate("/");
    }
  };

  /**
   * La sortie de secours de l'ecran `sign_in`.
   *
   * C'est le lien qui manquait. Un compte peut parfaitement exister sans que
   * son proprietaire connaisse le moindre mot de passe (pre-compte
   * d'invitation, rattachement fait par un exploitant) : sans ce chemin, la
   * page n'a aucune issue et l'invitation meurt d'expiration.
   */
  const handleForgotPassword = () => {
    const target = invitation?.email || email || "";
    if (!target) return;

    sendResetCode(
      { email: target },
      {
        onSuccess: () => {
          setResetSent(true);
          navigate(`/enter_code_forgot_password?email=${encodeURIComponent(target)}`);
        },
      }
    );
  };

  const handleSignIn = () => {
    const target = invitation?.email || email || "";
    if (signInPassword.length === 0) {
      toast.error(t("required.checkFields", "All fields are required"));
      return;
    }
    setDenial(null);
    login({ email: target, password: signInPassword });
  };

  const handleConfirm = () => {
    if (needsName && (firstname.trim().length === 0 || lastname.trim().length === 0)) {
      toast.error(t("required.checkFields", "All fields are required"));
      return;
    }
    if (!validatePassword(password, passwordPolicy)) {
      // Le message enonce les exigences REELLES du serveur, pas une phrase
      // figee qui se desynchronise des que la politique bouge.
      toast.error(describePolicy(t, passwordPolicy));
      return;
    }
    if (password !== passwordConfirm) {
      toast.error(t("login.errorConfirm", "Passwords do not match"));
      return;
    }

    register({
      password,
      "password-confirmation": passwordConfirm,
      code: code || "",
      // The validated invitation is authoritative; the query string is only a
      // fallback for the (impossible in practice) case where it hasn't loaded.
      email: invitation?.email || email || "",
      company: String(invitation?.company_id ?? company ?? ""),
      ...(needsName ? { firstname, lastname } : {}),
    });
  };

  // ---- missing / invalid invitation -------------------------------------
  if (!code || isError) {
    return (
      <div className={classes.container}>
        <Logo className={classes.logo} />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={t(
            "invitation.invalid",
            "This invitation link is invalid or has already been used.<br/>Please ask your fleet manager to send you a new invitation."
          )}
        />
        <div className={classes.buttonContainer}>
          <CustomButton
            onClick={() => navigate("/")}
            height={"40px"}
            variant={"white"}
            label={t("invitation.goToLogin", "GO TO LOGIN")}
          />
        </div>
      </div>
    );
  }

  // ---- validating the code ----------------------------------------------
  if (isLoading) {
    return (
      <div className={classes.container}>
        <Logo className={classes.logo} />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={t("invitation.loading", "Checking your invitation...")}
        />
      </div>
    );
  }

  // ---- expired / revoked invitation -------------------------------------
  if (nextAction === "expired" || nextAction === "revoked") {
    const isExpired = nextAction === "expired";
    return (
      <div className={classes.container}>
        <Logo className={classes.logo} />
        <div className={classes.successIcon} style={{ color: "#e74c3c" }}>✕</div>
        <CustomTypography
          variant="h3"
          styles={{ containerText: classes.companyName }}
          text={
            isExpired
              ? t("invitation.expiredTitle", "This invitation has expired")
              : t("invitation.revokedTitle", "This invitation has been revoked")
          }
        />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={
            isExpired
              ? t(
                  "invitation.expiredText",
                  "The invitation link is no longer valid. Please ask your fleet manager to resend the invitation."
                )
              : t(
                  "invitation.revokedText",
                  "This invitation has been cancelled by the administrator. Please contact your fleet manager."
                )
          }
        />
        {/*
          Une invitation morte n'implique pas un compte inutilisable : si la
          personne a deja un compte CodeNekt, elle peut entrer et se faire
          rattacher plus tard. Le bouton vers la connexion reste donc la
          bonne sortie, et la reinitialisation reste accessible depuis celle-ci.
        */}
        <div className={classes.buttonContainer}>
          <CustomButton
            onClick={() => navigate("/")}
            height={"40px"}
            variant={"white"}
            label={t("invitation.goToLogin", "GO TO LOGIN")}
          />
        </div>
      </div>
    );
  }

  // ---- already accepted / already a member ------------------------------
  if (nextAction === "already_accepted" || nextAction === "already_member") {
    const isMember = nextAction === "already_member";
    return (
      <div className={classes.container}>
        <Logo className={classes.logo} />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={
            isMember
              ? t(
                  "invitation.alreadyMember",
                  "You are already part of this company.<br/>Sign in to access your fleet."
                )
              : t(
                  "invitation.alreadyAccepted",
                  "This invitation has already been accepted.<br/>Please log in with your credentials."
                )
          }
        />
        <div className={classes.buttonContainer}>
          <CustomButton
            onClick={() => navigate("/")}
            height={"40px"}
            variant={"white"}
            label={t("invitation.login", "LOG IN")}
          />
        </div>
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.secondaryAction }}
          text={t("invitation.forgotPassword", "I don't know my password")}
          onClick={handleForgotPassword}
        />
      </div>
    );
  }

  // ---- fallback success screen ------------------------------------------
  // Atteint uniquement si l'acceptation a reussi sans qu'un jeton exploitable
  // revienne : l'effet ci-dessus aurait deja redirige sinon.
  if (isSuccess) {
    return (
      <div className={classes.container}>
        <Logo className={classes.logo} />
        <div className={classes.successIcon}>&#10003;</div>
        <CustomTypography
          variant="h3"
          styles={{ containerText: classes.companyName }}
          text={t("invitation.successTitle", "Your account is activated!")}
        />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={
            t("invitation.successText", "You have joined") +
            " " +
            (invitation?.company_name ?? "") +
            ". " +
            t(
              "invitation.successHint",
              "You can now log in with your email and your new password."
            )
          }
        />
        <div className={classes.buttonContainer}>
          <CustomButton
            onClick={() => navigate("/")}
            height={"40px"}
            variant={"white"}
            label={t("invitation.login", "LOG IN")}
          />
        </div>
      </div>
    );
  }

  // ---- an account already exists, with a password its owner knows --------
  //
  // Anciennement l'impasse : un message et un bouton vers la page de connexion.
  // Desormais la connexion se fait ICI, adresse pre-remplie, avec le lien de
  // reinitialisation a portee de main.
  if (nextAction === "sign_in") {
    return (
      <div className={classes.container}>
        <Logo className={classes.logo} />
        <CustomTypography
          variant="h3"
          styles={{ containerText: classes.companyName }}
          text={
            t("invitation.invitedTo", "You have been invited to join") +
            " " +
            (invitation?.company_name ?? "")
          }
        />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={t(
            "invitation.signInToJoin",
            "A CodeNekt account already exists for this address. Sign in to join the company — the link is made automatically."
          )}
        />
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.emailBadge }}
          text={invitation?.email ?? email ?? ""}
        />
        <div className={classes.subContainer}>
          <CustomTextField
            placeholder={t("login.password", "Password")}
            type={"password"}
            pass
            fullWidth={true}
            onChange={(e) => {
              setSignInPassword(e.target.value);
              if (denial) setDenial(null);
            }}
          />
          {denial && (
            <div className={classes.infoBox} role="alert">
              <CustomTypography
                variant="link"
                textDecoration={"none"}
                styles={{ containerText: classes.infoText }}
                text={t(
                  "invitation.signInDenied",
                  "You are signed in, but this account is not linked to any fleet yet. Contact your fleet manager."
                )}
              />
            </div>
          )}
          {resetSent && (
            <div className={classes.infoBox}>
              <CustomTypography
                variant="link"
                textDecoration={"none"}
                styles={{ containerText: classes.infoText }}
                text={t(
                  "invitation.resetSent",
                  "We sent a code to your address. Follow it to choose a new password."
                )}
              />
            </div>
          )}
          <div className={classes.buttonContainer}>
            <CustomButton
              onClick={handleSignIn}
              height={"40px"}
              variant={"white"}
              label={t("invitation.signIn", "SIGN IN AND JOIN")}
              disabled={isSigningIn}
              loading={isSigningIn}
            />
          </div>
          <CustomTypography
            variant="link"
            textDecoration={"none"}
            styles={{ containerText: classes.secondaryAction }}
            text={
              isSendingReset
                ? t("common.loading", "Loading...")
                : t("invitation.forgotPassword", "I don't know my password")
            }
            onClick={handleForgotPassword}
          />
          {isMobile && (
            <CustomTypography
              variant="link"
              textDecoration={"none"}
              styles={{ containerText: classes.secondaryAction }}
              text={t("invitation.openApp", "Open the CodeNekt mobile app")}
              onClick={handleOpenApp}
            />
          )}
        </div>
      </div>
    );
  }

  // ---- create password (and name when we don't know it) ------------------
  return (
    <div className={classes.container}>
      <Logo className={classes.logo} />
      <CustomTypography
        variant="h3"
        styles={{ containerText: classes.companyName }}
        text={
          t("invitation.invitedTo", "You have been invited to join") +
          " " +
          (invitation?.company_name ?? "")
        }
      />
      <CustomTypography
        variant="link"
        textDecoration={"none"}
        styles={{ containerText: classes.text }}
        text={t(
          "invitation.createPassword",
          "Create your password to confirm your addition and activate your account."
        )}
      />
      <CustomTypography
        variant="link"
        textDecoration={"none"}
        styles={{ containerText: classes.emailBadge }}
        text={invitation?.email ?? email ?? ""}
      />
      {invitation?.expires_at && (
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={
            t("invitation.expiresAt", "This link expires on") +
            " " +
            new Date(invitation.expires_at).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          }
        />
      )}
      <div className={classes.subContainer}>
        {needsName && (
          <>
            <CustomTextField
              placeholder={t("collaborator.firstName", "Firstname")}
              fullWidth={true}
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
            />
            <CustomTextField
              placeholder={t("collaborator.name", "Name")}
              fullWidth={true}
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
            />
          </>
        )}
        <CustomTextField
          placeholder={t("login.password", "Password")}
          type={"password"}
          pass
          fullWidth={true}
          onChange={(e) => setPassword(e.target.value)}
        />
        {/*
          Les exigences AVANT la saisie, pas apres le refus. Elles etaient
          annoncees uniquement par un toast rouge une fois le formulaire
          soumis : la personne tapait deux fois un mot de passe, se le faisait
          refuser, et devait deduire la regle du message.
        */}
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.text }}
          text={describePolicy(t, passwordPolicy)}
        />
        <CustomTextField
          placeholder={t("login.confirmPassword", "Confirm password")}
          type={"password"}
          pass
          fullWidth={true}
          onChange={(e) => setPasswordConfirm(e.target.value)}
        />
        <div className={classes.buttonContainer}>
          <CustomButton
            onClick={handleConfirm}
            height={"40px"}
            variant={"white"}
            label={t("invitation.confirm", "CONFIRM MY ADDITION")}
            loading={isPending}
          />
        </div>
        {/*
          Meme sur cet ecran : si la personne se rend compte qu'elle a deja un
          compte, ou si l'API refuse en 409 parce qu'un mot de passe existe
          deja, il lui faut une sortie qui ne passe pas par le support.
        */}
        <CustomTypography
          variant="link"
          textDecoration={"none"}
          styles={{ containerText: classes.secondaryAction }}
          text={t("invitation.alreadyHaveAccount", "I already have an account")}
          onClick={() => navigate("/")}
        />
      </div>
    </div>
  );
};
